/**
 * Formats a number with thousand separators.
 * @param num - The number to format.
 * @param separator - The character to use as a thousand separator (default is ',').
 * @returns The formatted string.
 */
export const formatWithThousandSeparator = (
  num: number | string,
  separator: string = ',',
): string => {
  // Remove non-numeric characters except '.' to allow decimal points
  const value = typeof num === 'number' ? `${num}` : num;
  const cleanedNum = value.replace(/[^\d.]/g, '');

  // Split integer and decimal parts
  const [integerPart, decimalPart] = cleanedNum.split('.');

  // Format the integer part with separators
  const formattedInteger = integerPart!.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    separator,
  );

  // Combine the formatted integer part with the decimal part
  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
};

/**
 * Abbreviates a number to a string with a unit (k, M, B, T).
 * @param num - The number to abbreviate.
 * @returns The abbreviated number as a string.
 */
export function abbreviateNumber(num: number, decimalPoints: number = 1): string {
  if (num < 1000) {
    return num.toString();
  }

  const units = ['K', 'M', 'B', 'T'];
  let unitIndex = -1;
  let value = num;

  while (value >= 1000 && unitIndex < units.length - 1) {
    value /= 1000;
    unitIndex++;
  }

  // Keep 1 decimal place, but remove trailing .0
  const formatted = value.toFixed(decimalPoints).replace(/\.0$/, '');
  return formatted + units[unitIndex];
}

/**
 * Removes any non-numeric and non-comma characters from a given string value, and optionally converts the
 * result to a number.
 * @param {object} options - Options for the function.
 * @param {string} options.value - The string value containing the dollar sign.
 * @param {boolean} [options.toNumber] - When true, the result is converted
 *   to a number.
 * @returns {number|string} The result of removing the special characters, and optionally
 *   converting the result to a number.
 */
export function extractNumberString({
  toNumber,
  value,
}: {
  value: string;
  toNumber?: boolean;
}): number | string {
  // strip all non-numeric, non-comma and non-dot characters
  const cleaned = value.replace(/[^\d,.]/g, '');

  // if toNumber is true, convert the result to a number (remove commas)
  return toNumber ? Number(unformatNumber(cleaned)) : cleaned;
}

/**
 * Formats a number or string to a specified number of decimal points.
 * @param value - The number or string to format.
 * @param decimalPoints - The number of decimal points to format to.
 * @returns The formatted number as a string.
 */
export function formatDecimal(value: number, decimalPoints: number): string {
  return value.toFixed(decimalPoints);
}

/**
 * Converts the input value to an integer, removing all non-digit characters.
 * @param val The value to convert to an integer.
 * @returns The integer representation of the input value, or 0 if conversion fails.
 */
export function toInt(val: any): number {
  return Number.parseInt(String(val).replace(/\D/g, ''), 10) || 0;
}

/**
 * Removes commas from a number string.
 * @param value - The number string to unformat.
 * @returns The unformatted number string.
 */
export function unformatNumber(value: string): string {
  return value.replace(/,/g, '');
}
